/** Axis helpers: round tick values and a domain that lands on them, so every gridline carries a readable value. */

/** Steps that read well on a clock: seconds, then minutes, then hours. */
const TIME_STEPS = [1, 2, 5, 10, 15, 20, 30, 60, 120, 300, 600, 900, 1200, 1800, 3600, 7200, 10800, 21600];

export interface NiceAxis {
  domain: [number, number];
  ticks: number[];
}

function build(min: number, max: number, step: number): NiceAxis {
  const start = Math.floor(min / step) * step;
  // A single value still gets an axis: at least one step above the start.
  const end = Math.max(Math.ceil(max / step) * step, start + step);
  const ticks: number[] = [];
  // Rounding the multiplier keeps decimals like 0.1 + 0.2 out of the labels.
  for (let i = 0; start + i * step <= end + step / 1e6; i++) ticks.push(Math.round((start + i * step) * 1e6) / 1e6);
  return { domain: [ticks[0], ticks[ticks.length - 1]], ticks };
}

/** Ticks for a duration axis (seconds): steps of 5 s, 30 s, 5 min ... so labels are 4:50, 5:00, 5:10. */
export function timeAxis(min: number, max: number, target = 7): NiceAxis {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  const range = Math.max(hi - lo, 1);
  const step = TIME_STEPS.find(s => range / s <= target) ?? TIME_STEPS[TIME_STEPS.length - 1];
  return build(lo, hi, step);
}

/** Ticks for a plain number axis: steps of 1, 2 or 5 times a power of ten. */
export function numberAxis(min: number, max: number, target = 7): NiceAxis {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  const range = Math.max(hi - lo, 1e-9);
  const rough = range / target;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 5, 10].map(m => m * magnitude).find(s => range / s <= target) ?? 10 * magnitude;
  return build(lo, hi, step);
}

/** Ticks and domain for the min and max of a list of values, ignoring gaps. Null when there is nothing to plot. */
export function axisFor(values: Array<number | null | undefined>, kind: 'time' | 'number', target = 7): NiceAxis | null {
  const finite = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (finite.length === 0) return null;
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  return kind === 'time' ? timeAxis(min, max, target) : numberAxis(min, max, target);
}

/** Props shared by every value axis: enough ticks to read a value off, from a muted text style. */
export const Y_AXIS = { stroke: 'var(--text-muted)', fontSize: 12, tickCount: 7, tickMargin: 4 } as const;

interface YOptions {
  /** 'time' for seconds shown as a clock, else a plain number. */
  kind?: 'time' | 'number';
  /** Always include 0 (bars, counts). */
  zero?: boolean;
  /** A fixed range such as 0-100 (scores, percentages). */
  fixed?: [number, number];
  /** Roughly how many ticks. */
  target?: number;
}

/**
 * domain and ticks for a value axis, from the rows the chart draws and the keys it plots. Spread into a YAxis:
 * {...Y_AXIS} {...yAxisProps(rows, ['pace'], { kind: 'time' })}. Without data it returns nothing, so the chart keeps its
 * own defaults.
 */
export function yAxisProps(
  rows: ReadonlyArray<object> | undefined,
  keys: string[],
  options: YOptions = {}
): { domain?: [number, number]; ticks?: number[]; interval?: 0 } {
  const kind = options.kind ?? 'number';
  if (options.fixed) {
    const axis = numberAxis(options.fixed[0], options.fixed[1], options.target ?? 5);
    return { domain: options.fixed, ticks: axis.ticks, interval: 0 };
  }
  const values = (rows ?? []).flatMap(row => keys.map(k => (row as Record<string, unknown>)[k])) as Array<number | null>;
  const finite = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (finite.length === 0) return {};
  const axis = axisFor(options.zero ? [0, ...finite] : finite, kind, options.target ?? 7);
  return axis ? { domain: axis.domain, ticks: axis.ticks, interval: 0 } : {};
}
